// Playing Game Shell component for Game and Playing pages

import React, { useMemo } from 'react';
import Background from '@/components/games/scene/Background';
import { LinearCountdownBar } from '@/components/games';
import MusicDisplay from '@/components/media/MusicDisplay';
import {
  TransitionMessage,
  ROOM_LIFECYCLE,
} from '../components/transitionMessage';
import { Poppy } from '../components/avatars';
import { SkipIntroButton } from '@/components/games';
import {
  useAnalyticsHeartbeat,
  useRoomInteractionWindow,
  useRoomStatusGuard,
} from '../hooks';
import { getGameCodeFolder } from '@airhead-games/shared/utilities';

const GAMES_WITHOUT_DEFAULT_POPPY: ReadonlySet<string> = new Set([
  'EgoManiacs', // Poppy moves around during game
  'SecondOpinions', // Poppy is embedded in the health meter
]);
const GAMES_WITHOUT_DEFAULT_BACKGROUND: ReadonlySet<string> = new Set([
  'MemeMash', // Whole screen camera zoom animation
]);

type PlayingGameShellProps = {
  role: 'host' | 'player';
};

export const PlayingGameShell: React.FC<PlayingGameShellProps> = ({ role }) => {
  useAnalyticsHeartbeat(role);

  const { gameId, gate } = useRoomStatusGuard();
  const { windowStatus, windowId, windowEndTime } = useRoomInteractionWindow();
  const gameCodeFolder = gameId ? getGameCodeFolder(gameId) : undefined;
  const controllerFolder = role === 'host' ? 'Game' : 'Playing';

  const Controller = useMemo(
    () =>
      gameCodeFolder
        ? React.lazy(
            () =>
              import(
                `../games/${gameCodeFolder}/${controllerFolder}/${controllerFolder}Controller.tsx`
              )
          )
        : null,
    [controllerFolder, gameCodeFolder]
  );

  if (gate) {
    return <div className="relative h-[100svh] w-full">{gate}</div>;
  }

  const showDefaultPoppy =
    !gameCodeFolder || !GAMES_WITHOUT_DEFAULT_POPPY.has(gameCodeFolder);
  const showDefaultBackground =
    role === 'host' &&
    (!gameCodeFolder || !GAMES_WITHOUT_DEFAULT_BACKGROUND.has(gameCodeFolder));

  return (
    <div className="relative h-[100svh] w-full overflow-hidden overscroll-none">
      {showDefaultBackground && <Background />}
      <React.Suspense
        fallback={
          <TransitionMessage
            messages={ROOM_LIFECYCLE.connectingToRoom}
            showSpinner
            enableFunnyMessages
          />
        }
      >
        {Controller && <Controller />}
      </React.Suspense>

      {windowStatus === 'opened' && (
        <LinearCountdownBar
          key={windowId}
          endTime={windowEndTime}
          timerTick={role === 'host'}
        />
      )}

      {role === 'host' && showDefaultPoppy && (
        <div className="fixed right-0 top-0 z-[2000] flex w-[240px] flex-col items-center">
          <Poppy size={240} />
          <SkipIntroButton />
        </div>
      )}
      {role === 'host' && <MusicDisplay />}
    </div>
  );
};
